import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import {
  Target, Zap, Shield, ArrowRight, Leaf,
  Star, Compass, Award, Heart, Globe2, Users, ShieldCheck,
} from 'lucide-react';
import {
  GET_ABOUT_STATS, GET_TEAM_MEMBERS, GET_COMPANY_MILESTONES,
  GET_PRESS_PARTNERS, GET_ABOUT_PAGE_SETTINGS, GET_COMPANY_VALUES,
} from '../graphql/coresite';

const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

/* ─── Icon registry for CompanyValue tiles ─── */
const ICON_MAP = {
  Target, Zap, Shield, Leaf, Star, Compass,
  Award, Heart, Globe2, Users, ShieldCheck,
};
const getIcon = (name) => ICON_MAP[name] || Compass;

const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');
    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }
    .grain { background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E"); }
    .gold-rule { height: 1px; background: linear-gradient(90deg, ${GOLD} 0%, rgba(184,151,90,0.1) 100%); }
    .stat-card { border: 1px solid #e2ddd6; background: #fff; transition: transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s; }
    .stat-card:hover { transform: translateY(-3px); box-shadow: 0 16px 48px rgba(0,0,0,0.09); }
    .value-tile { border: 1px solid #e2ddd6; background: #fff; padding: 28px; transition: all 0.35s cubic-bezier(0.22,1,0.36,1); }
    .value-tile:hover { border-color: ${GOLD}; }
    .value-icon { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border: 1px solid ${GOLD_DIM}; margin-bottom: 20px; transition: all 0.35s; }
    .value-tile:hover .value-icon { border-color: ${GOLD}; background: ${GOLD_DIM}; }
    .team-card { overflow: hidden; transition: transform 0.4s cubic-bezier(0.22,1,0.36,1); }
    .team-card:hover { transform: translateY(-3px); }
    .team-img { transition: transform 0.8s cubic-bezier(0.22,1,0.36,1); width: 100%; height: 100%; object-fit: cover; filter: grayscale(40%); }
    .team-card:hover .team-img { filter: grayscale(0%); transform: scale(1.05); }
    .primary-cta { position: relative; overflow: hidden; display: inline-flex; align-items: center; gap: 10px; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 15px 32px; background: ${NAVY}; color: #fff; border: 1px solid ${NAVY}; cursor: pointer; transition: all 0.35s cubic-bezier(0.22,1,0.36,1); text-decoration: none; }
    .primary-cta::before { content: ''; position: absolute; inset: 0; background: ${GOLD}; transform: scaleX(0); transform-origin: left; transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0; }
    .primary-cta:hover::before { transform: scaleX(1); }
    .primary-cta:hover { color: ${NAVY}; border-color: ${GOLD}; }
    .primary-cta span, .primary-cta svg { position: relative; z-index: 1; }
    .ghost-cta { display: inline-flex; align-items: center; gap: 8px; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 15px 32px; border: 1px solid #ddd9d2; color:#12100e; background: #fff; cursor: pointer; transition: all 0.3s; text-decoration: none; }
    .ghost-cta:hover { border-color: ${NAVY}; color: ${NAVY}; }
    @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
    .fade-up { animation: fadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both; }
    .hero-1 { animation: fadeUp 1s cubic-bezier(0.22,1,0.36,1) 0.2s both; }
    .hero-2 { animation: fadeUp 1s cubic-bezier(0.22,1,0.36,1) 0.4s both; }
    .hero-3 { animation: fadeUp 0.8s cubic-bezier(0.22,1,0.36,1) 0.6s both; }
    .timeline-row { position: relative; padding-left: 48px; padding-bottom: 36px; }
    .timeline-row::before { content: ''; position: absolute; left: 11px; top: 28px; bottom: 0; width: 1px; background: linear-gradient(to bottom, ${GOLD}, rgba(184,151,90,0.05)); }
    .timeline-row:last-child::before { display: none; }
    .timeline-dot { position: absolute; left: 0; top: 16px; width: 24px; height: 24px; border: 1px solid ${GOLD}; background: #F5F3EF; display: flex; align-items: center; justify-content: center; }
  `}</style>
);

const SectionLabel = ({ text, light }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="w-6 h-px" style={{ background: light ? 'rgba(184,151,90,0.6)' : GOLD }} />
    <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]"
      style={{ color: light ? 'rgba(184,151,90,0.7)' : GOLD }}>{text}</span>
  </div>
);

/* ─── Skeleton for stat cards ─── */
const StatSkeleton = () => (
  <div className="stat-card p-7 text-center">
    {[60, 32, 80, 50].map((w, i) => (
      <div key={i} style={{ height: '12px', width: `${w}%`, background: '#f0ece6', margin: '0 auto 10px', borderRadius: '2px' }} />
    ))}
  </div>
);

const AboutPage = () => {
  const { data: settingsData } = useQuery(GET_ABOUT_PAGE_SETTINGS);
  const { data: statsData }    = useQuery(GET_ABOUT_STATS);
  const { data: teamData }     = useQuery(GET_TEAM_MEMBERS);
  const { data: milestonesData } = useQuery(GET_COMPANY_MILESTONES);
  const { data: partnersData } = useQuery(GET_PRESS_PARTNERS);
  const { data: valuesData }   = useQuery(GET_COMPANY_VALUES);

  const pg         = settingsData?.aboutPageSettings;
  const statsRaw   = statsData?.aboutStats;
  const team       = teamData?.teamMembers        || [];
  const milestones = milestonesData?.companyMilestones || [];
  const partners   = partnersData?.pressPartners  || [];
  const values     = valuesData?.companyValues     || [];

  const stats = statsRaw ? [
    {
      value: statsRaw.travellers,
      label: 'Travellers served',
      sub:   statsRaw.yearsOperating ? `since ${new Date().getFullYear() - statsRaw.yearsOperating}` : '',
    },
    {
      value: `${statsRaw.totalCountries}`,
      label: 'Countries',
      sub:   'in East Africa',
    },
    {
      value: `${statsRaw.averageRating}`,
      label: 'Average guest rating',
      sub:   'across all journeys',
    },
    {
      value: `${statsRaw.yearsOperating}+`,
      label: 'Years of excellence',
      sub:   'designing safaris',
    },
  // A stat with nothing behind it yet (no bookings, no reviews) reads as a
  // flaw on a sales page — leave it out until there's a real number.
  ].filter(s => s.value && !['0', '0+', '0.0', 'undefined', 'null'].includes(String(s.value))) : [];

  return (
    <div className="f-jost min-h-screen" style={{ background: '#F5F3EF' }}>
      <PageStyles />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden dark-sec" style={{ background: NAVY, height: '88vh', minHeight: '560px', display: 'flex', alignItems: 'center' }}>
        <img
          src={pg?.heroImage || 'https://images.unsplash.com/photo-1452421822248-d4c2b47f0c81?w=1920&q=80'}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(110deg, rgba(8,14,26,0.92) 40%, rgba(8,14,26,0.5))' }} />
        <div className="grain absolute inset-0 pointer-events-none opacity-60" />
        <div className="absolute left-0 top-0 bottom-0 hidden lg:block" style={{ marginLeft: '10%', width: '1px', background: `linear-gradient(to bottom, transparent, ${GOLD} 30%, ${GOLD} 70%, transparent)`, opacity: 0.2 }} />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 w-full">
          <div className="max-w-3xl">
            <div className="hero-1 flex items-center gap-3 mb-7">
              <div className="w-8 h-px" style={{ background: GOLD }} />
              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                {pg?.heroEyebrow || 'Safari specialists'}
              </span>
            </div>
            <h1 className="f-cormorant text-white font-light leading-[0.92] mb-6 hero-2"
              style={{ fontSize: 'clamp(58px,7.5vw,96px)' }}>
              {pg?.heroHeadingLine1 || 'The Art of'}<br />
              <span style={{ color: GOLD }}>{pg?.heroHeadingLine2 || 'Discovery.'}</span>
            </h1>
            <p className="f-jost text-slate-400 text-[16px] font-light leading-relaxed max-w-md mb-10 hero-3">
              {pg?.heroBody || 'Private safaris across East Africa, planned by people who live here — the right camps, the right season, and guides who know the land.'}
            </p>
            <div className="hero-3 flex flex-wrap gap-4">
              <Link to="/tours" className="primary-cta"><span>Explore Expeditions</span><ArrowRight size={12} /></Link>
              <Link to="/contact" className="ghost-cta"><span>Speak to a Specialist</span></Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-30">
          <div className="w-px h-10 animate-pulse" style={{ background: GOLD }} />
          <span className="f-jost text-[12px] uppercase tracking-[0.2em] text-white">Scroll</span>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <section className="relative z-20 -mt-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.length > 0 ? stats.map((s, i) => (
              <div key={i} className="stat-card p-7 text-center fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <p className="f-cormorant font-light leading-none mb-2" style={{ fontSize: '46px', color: NAVY }}>{s.value}</p>
                <div className="gold-rule mx-auto mb-3" style={{ width: '32px' }} />
                <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] text-slate-700 mb-1">{s.label}</p>
                <p className="f-jost text-[13px] text-slate-400 font-light">{s.sub}</p>
              </div>
            )) : Array.from({ length: 4 }).map((_, i) => <StatSkeleton key={i} />)}
          </div>
        </div>
      </section>

      {/* ── Story ── */}
      <section className="py-24 lg:py-32" style={{ background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ aspectRatio: '4/5' }}>
                <img
                  src={pg?.storyImage || 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1000&q=80'}
                  alt="Legacy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(8,14,26,0.35), transparent 60%)' }} />
              </div>
              <div className="absolute -bottom-5 -right-5 sm:right-8 p-7 hidden sm:block" style={{ background: NAVY, border: `1px solid rgba(184,151,90,0.25)` }}>
                <p className="f-cormorant text-white font-light leading-none" style={{ fontSize: '52px', color: GOLD }}>
                  {statsRaw?.yearsOperating || 25}
                </p>
                <p className="f-jost text-[13px] uppercase tracking-[0.2em] mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Years of Excellence</p>
              </div>
              <div className="absolute top-5 left-5 w-10 h-10 pointer-events-none" style={{ borderTop: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}` }} />
            </div>

            <div>
              <SectionLabel text={pg?.storyEyebrow || 'Our Heritage'} />
              <h2 className="f-cormorant font-light leading-[0.95] mb-6"
                style={{ fontSize: 'clamp(40px,4.5vw,58px)', color: NAVY }}>
                {pg?.storyHeading || 'Born from a restless spirit.'}
              </h2>
              <p className="f-jost text-slate-600 text-[16px] font-light leading-relaxed mb-6">
                {pg?.storyBody1 || 'Founded in a small London studio in 1998, we set out to dismantle the factory-made travel industry.'}
              </p>
              <p className="f-jost text-slate-500 text-[16px] font-light leading-relaxed mb-8">
                {pg?.storyBody2 || 'Today we operate across 38 countries — but our philosophy has never changed.'}
              </p>
              {pg?.pullQuote && (
                <div className="border-l-2 pl-6 py-2" style={{ borderColor: GOLD }}>
                  <p className="f-cormorant text-slate-700 font-light leading-relaxed" style={{ fontSize: '25px' }}>
                    "{pg.pullQuote}"
                  </p>
                  <p className="f-jost text-[13px] uppercase tracking-[0.2em] text-slate-400 mt-3">
                    — {pg.pullQuoteBy}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Milestones ── */}
      <section className="py-24 lg:py-32" style={{ background: '#F5F3EF', borderTop: '1px solid #e2ddd6' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <SectionLabel text={pg?.milestonesEyebrow || 'Our Journey'} />
              <h2 className="f-cormorant font-light leading-[0.95]"
                style={{ fontSize: 'clamp(40px,4vw,54px)', color: NAVY }}>
                {/* Defaults to the real yearsOperating figure (same source as
                    the stats strip above) rather than a hardcoded number that
                    silently drifts out of sync as time passes. */}
                {pg?.milestonesHeadingLine1 || `${statsRaw?.yearsOperating || 25}+ years`}<br />
                {pg?.milestonesHeadingLine2 || 'in the making.'}
              </h2>
            </div>
            <div className="mt-4">
              {milestones.map((m, i) => (
                <div key={m.id} className="timeline-row">
                  <div className="timeline-dot">
                    <div className="w-2 h-2 rounded-full" style={{ background: GOLD }} />
                  </div>
                  <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] block mb-1" style={{ color: GOLD }}>{m.year}</span>
                  <h3 className="f-cormorant text-slate-900 font-light text-[27px] leading-snug mb-1">{m.title}</h3>
                  <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">{m.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Values / Philosophy ── */}
      <section className="py-24 lg:py-32 dark-sec" style={{ background: NAVY }}>
        <div className="grain absolute inset-0 pointer-events-none opacity-60" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <div>
              <SectionLabel text={pg?.philosophyEyebrow || 'Our Philosophy'} light />
              <h2 className="f-cormorant text-white font-light leading-[0.95]"
                style={{ fontSize: 'clamp(40px,4.5vw,58px)' }}>
                {pg?.philosophyHeading || 'How we plan every safari.'}
              </h2>
            </div>
            {pg?.philosophySub && (
              <p className="f-jost text-slate-500 text-[15px] font-light max-w-sm lg:mb-2">{pg.philosophySub}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {values.map((v) => {
              const Icon = getIcon(v.icon);
              return (
                <div key={v.id} className="value-tile"
                  style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.07)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(184,151,90,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                >
                  <div className="value-icon" style={{ borderColor: 'rgba(184,151,90,0.25)' }}>
                    <Icon size={14} strokeWidth={1.5} style={{ color: GOLD }} />
                  </div>
                  <h3 className="f-cormorant text-white font-light text-[27px] leading-snug mb-3">{v.title}</h3>
                  <p className="f-jost text-slate-500 text-[15px] font-light leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="py-24 lg:py-32" style={{ background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <div>
              <SectionLabel text={pg?.teamEyebrow || 'The Team'} />
              <h2 className="f-cormorant font-light leading-none"
                style={{ fontSize: 'clamp(40px,4vw,54px)', color: NAVY }}>
                {pg?.teamHeading || 'Meet the team'}
              </h2>
            </div>
            <p className="f-jost text-[15px] text-slate-500 font-light max-w-xs sm:text-right sm:mb-1">
              {pg?.teamSub || `A small, impossibly experienced team built over ${statsRaw?.yearsOperating || 25}+ years of fieldwork.`}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {team.map((member, i) => (
              <div key={member.id} className="team-card">
                <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  <img src={member.photo} alt={member.name} className="team-img" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(8,14,26,0.65) 20%, transparent 60%)' }} />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>{member.role}</p>
                    <h4 className="f-cormorant text-white font-light text-[25px] leading-snug">{member.name}</h4>
                  </div>
                  <div className="absolute top-3 right-3 w-6 h-6 pointer-events-none" style={{ borderTop: `1px solid ${GOLD}`, borderRight: `1px solid ${GOLD}` }} />
                </div>
                <div className="p-5 border-x border-b" style={{ borderColor: '#e8e4de', background: '#FAFAF8' }}>
                  <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Press partners ── */}
      {partners.length > 0 && (
        <section className="border-t border-b py-10" style={{ borderColor: '#e2ddd6', background: '#F5F3EF' }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <p className="f-jost text-[13px] uppercase tracking-[0.2em] text-slate-400 flex-shrink-0">As seen in</p>
              <div className="w-px h-5 bg-slate-300 hidden sm:block" />
              {partners.map((p) => (
                p.logoUrl ? (
                  <img key={p.id} src={p.logoUrl} alt={p.name} style={{ height: '24px', opacity: 0.5, filter: 'grayscale(100%)' }} />
                ) : (
                  <span key={p.id} className="f-cormorant text-slate-400 font-light" style={{ fontSize: '22px' }}>{p.name}</span>
                )
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="py-24 lg:py-32" style={{ background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center border p-10 sm:p-16" style={{ borderColor: '#e2ddd6' }}>
            <div>
              <SectionLabel text={pg?.ctaEyebrow || 'Begin'} />
              <h2 className="f-cormorant font-light leading-[0.95] mb-4"
                style={{ fontSize: 'clamp(40px,4vw,54px)', color: NAVY }}>
                {pg?.ctaHeading || 'Ready for your next chapter?'}
              </h2>
              <p className="f-jost text-slate-500 text-[15px] font-light leading-relaxed">
                {pg?.ctaBody || 'Every safari starts with a conversation. Our specialists are ready to help you plan yours.'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="primary-cta"><span>Request a Consultation</span><ArrowRight size={12} /></Link>
              <Link to="/tours" className="ghost-cta"><span>Browse Expeditions</span></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;